import Date from "../Inputs/Date";
import { Backdrop, Button, CircularProgress, Grid } from "@mui/material";
import { BudgetItem, BuildReportColumnLabels, CurrentUser, CustomReportFilter, CustomReportSettings, DateTime, FamilyFeatureValue, GetEnabledBucketDefinitions, Grouping, SelectableItem, TransactionItem, USCurrency, UserItem, BudgetTransaction } from "../../types";
import { useMemo, useState } from "react";
import Dropdown from "../Inputs/Dropdown";
import { Features } from "../../constants";
import { useSelector } from "react-redux";
import ModalHeader from "./ModalHeader";
import ModalFooterCustom from "./ModalFooterCustom";
import { DownloadPdf } from "../../features/ReportService";
import { useItems } from "../../features/Fetch/Items";
import ReportPreviewTables from "../Reports/ReportPreviewTables";
import { buildReportPreview } from "../../features/Reports/preview";

const UserReport = (props: any) => {
	let budgets: BudgetItem[] = props.Budgets;
	let transactions: TransactionItem[] = props.Transactions;
	let users: UserItem[] = props.Users;
	let user: CurrentUser = useSelector((state: any) => state.userData.value);

	const bucketColumnKeys = useMemo(() => GetEnabledBucketDefinitions(user)
		.filter(d => d.bucketTypeKey?.toLowerCase() !== 'budget')
		.map(d => `Bucket:${d.bucketTypeKey}`), [user]);
	const columns = useMemo(() => ["Date", "Merchant", "Budget", ...bucketColumnKeys, "Description", "Amount"], [bucketColumnKeys]);

	let [startDate, setStartDate] = useState(props.StartDate as DateTime);
	let [endDate, setEndDate] = useState(props.EndDate as DateTime);
	let [selectedUsers, setSelectedUsers] = useState(users.map((u: UserItem) => u.userAccountId));
	let [overlay, setOverlay] = useState(false);
	const { groups: budgetGroups } = buildReportPreview({
		transactions: transactions,
		budgets: budgets,
		users: users,
		settings: {
			title: users.length === 1 && users[0].isMe ? `My ${props.IsMileage ? 'Mile' : FamilyFeatureValue(user, Features.TransactionTitle)}s` : 'By User',
			columns,
			grouping: 'User',
			filter: {
				startDate: startDate,
				endDate: endDate,
				createdById: selectedUsers,
				includeSpending: !props.IsMileage,
				includeMiles: props.IsMileage,
				includeCheckRequests: false,
				combineSplitBudgets: false,
				includeReceipts: true,
				groupReceipts: false
			} as CustomReportFilter
		} as CustomReportSettings
	});

	let repo = useItems();

	let allUsersSelected = selectedUsers.length === users.length;

	return (<>
		<ModalHeader Title={users.length === 1 && users[0].isMe ? `My ${props.IsMileage ? 'Mile' : FamilyFeatureValue(user, Features.TransactionTitle)}s` : 'By User'} CanDelete={false} Close={props.Close} />
		<div className="modal-body mt-5">
			<Grid container spacing={1}>
				<Grid item xs={12}>
					<Button onClick={() => Export(budgetGroups)}>
						Download Report
					</Button>
				</Grid>
				<Date Size={4} Label="From" Value={startDate} OnChange={(v: any) => setStartDate(new DateTime(v))} />
				<Date Size={4} Label="To" Value={endDate} OnChange={(v: any) => setEndDate(new DateTime(v))} />
				{!props.IsMe &&
					<Dropdown Size={4}
						Class="mb-0"
						Label="Include Users"
						Options={users
							.map(u => {
								return {
									Id: u.userAccountId,
									Value: `${u.firstName} ${u.lastName}`
								} as SelectableItem;
							})}
						Index={0}
						Value={allUsersSelected ? [] : selectedUsers}
						Multiple
						OnChange={UpdateUsers} />
				}
				<Grid item xs={8} />
				<Grid item xs={4} >
					<h1 className="report-total">{USCurrency(budgetGroups.reduce((total: number, g: Grouping) => g.Value.reduce((sub: number, b: BudgetTransaction) => b.Amount.amount + sub, 0) + total, 0))}</h1>
				</Grid>
			</Grid>

			<ReportPreviewTables
				groups={budgetGroups}
				columns={columns}
				grouping="User"
			/>
		</div>
		<ModalFooterCustom HideSave={true} Close={props.Close} />
		<Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={overlay}>
			Generating Report...<br />
			<CircularProgress />
		</Backdrop>
	</>);


	function UpdateUsers(newValue: number[]) {
		if (newValue.length === 0) {
			newValue = users.map((u: UserItem) => u.userAccountId);
		}
		setSelectedUsers(newValue);
	}

	async function Export(items: Grouping[]) {
		setOverlay(true);
		setTimeout(() => GenerateReport(items), 50);
	}

	async function GenerateReport(_items: Grouping[]) {
		let includedUsers = users.filter(u => selectedUsers.includes(u.userAccountId));
		let name = `${props.IsMe ? 'My ' : ''}${props.IsMileage ? 'Mileage' : `${FamilyFeatureValue(user, Features.TransactionTitle)}s`}${props.IsMe ? '' : ' By User'}`;
		let settings: CustomReportSettings = {
			title: name,
			columns: columns,
			columnLabels: BuildReportColumnLabels(user, columns),
			grouping: 'User',
			filter: {
				startDate: startDate,
				endDate: endDate,
				createdById: includedUsers.map(i => i.userAccountId),
				includeSpending: !props.IsMileage,
				includeMiles: props.IsMileage,
				includeCheckRequests: false,
				combineSplitBudgets: false,
				includeReceipts: true,
				groupReceipts: false
			} as CustomReportFilter
		}
		let file = await repo.GetReportPdfAsync(settings);
		DownloadPdf(await file.blob() as Blob, name);
		setOverlay(false);
	}
}

export default UserReport;